// src/components/Navbar.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import { useUserTier } from '../context/UserTierContext';
import { supabase } from '../supabase';

const NAV_LINKS = [
  { label: 'Challenges',     path: '/challenges/apex' },
  { label: 'Flashcards',     path: '/flashcards'      },
  { label: 'Apex30',         path: '/apex30'          },
  { label: 'Interview Prep', path: '/interview'       },
  { label: 'Leaderboard',    path: '/leaderboard'     },
  { label: 'My Progress',    path: '/profile'         },
];

const MORE_LINKS = [
  { label: 'FAQ',     path: '/faq'     },
  { label: 'Contact', path: '/contact' },
  { label: 'Pricing', path: '/pricing' },
];

export default function Navbar() {
  const navigate          = useNavigate();
  const location          = useLocation();
  const { user }          = useAuth();
  const { theme, toggle } = useTheme();
  const { tier }          = useUserTier();
  const c                 = TOKENS[theme];

  const [menuOpen,        setMenuOpen]        = useState(false);
  const [moreOpen,        setMoreOpen]        = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const isLight   = theme === 'light';
  const tierLabel = tier === 'admin' ? '👑 Admin' : tier === 'pro' ? '⚡ Pro' : tier === 'team' ? '👥 Team' : null;
  const isActive  = (path) => location.pathname === path || location.pathname.startsWith(path.split('/').slice(0, 2).join('/'));

  const handleLogout = async () => {
    setIsDisconnecting(true);
    await supabase.auth.signOut();
    navigate('/');
  };

  const linkStyle = (path) => ({
    fontSize: 13, padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
    color: isActive(path) ? c.accent : c.secondary,
    fontWeight: isActive(path) ? 600 : 400,
    background: isActive(path) ? c.accentBg : 'transparent',
    transition: 'color 0.15s, background 0.15s',
    whiteSpace: 'nowrap',
    border: 'none',
  });

  return (
    <nav style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0 clamp(16px, 4vw, 60px)', height: 58,
      background: c.navBg, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${c.border}`,
      position: 'sticky', top: 0, zIndex: 100,
      transition: 'background 0.25s, border-color 0.25s',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>

      {/* Logo */}
      <span onClick={() => navigate(user ? '/dashboard' : '/')}
        style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.5px', color: c.text, cursor: 'pointer', flexShrink: 0 }}>
        Trail<span style={{ color: c.accent }}>Code</span>
      </span>

      {/* Desktop nav links */}
      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }} className="tc-nav-desktop">
        {NAV_LINKS.map(({ label, path }) => (
          <span key={path} onClick={() => navigate(path)} style={linkStyle(path)}
            onMouseEnter={e => { if (!isActive(path)) { e.target.style.color = c.text; e.target.style.background = c.card; } }}
            onMouseLeave={e => { if (!isActive(path)) { e.target.style.color = c.secondary; e.target.style.background = 'transparent'; } }}>
            {label}
          </span>
        ))}

        {/* More dropdown */}
        <div style={{ position: 'relative' }}>
          <span onClick={() => setMoreOpen(o => !o)}
            style={{ ...linkStyle('/more'), padding: '5px 10px', userSelect: 'none' }}
            onMouseEnter={e => { e.target.style.color = c.text; e.target.style.background = c.card; }}
            onMouseLeave={e => { if (!moreOpen) { e.target.style.color = c.secondary; e.target.style.background = 'transparent'; } }}>
            More ▾
          </span>
          {moreOpen && (
            <div style={{ position: 'absolute', top: 36, right: 0, background: c.navBg, border: `1px solid ${c.border}`, borderRadius: 10, padding: 6, minWidth: 140, zIndex: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
              onMouseLeave={() => setMoreOpen(false)}>
              {MORE_LINKS.map(({ label, path }) => (
                <div key={path} onClick={() => { navigate(path); setMoreOpen(false); }}
                  style={{ fontSize: 13, padding: '8px 12px', borderRadius: 7, cursor: 'pointer', color: isActive(path) ? c.accent : c.text, background: isActive(path) ? c.accentBg : 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = c.card}
                  onMouseLeave={e => e.currentTarget.style.background = isActive(path) ? c.accentBg : 'transparent'}>
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>

        {/* Theme toggle */}
        <button onClick={toggle} aria-label="Toggle theme"
          style={{ width: 46, height: 26, background: c.card, border: `1px solid ${c.border}`, borderRadius: 13, cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
          <div style={{ position: 'absolute', top: 3, left: isLight ? 21 : 3, width: 18, height: 18, background: isLight ? '#f59e0b' : c.accent, borderRadius: '50%', transition: 'left 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
            {isLight ? '☀️' : '🌙'}
          </div>
        </button>

        {/* User — desktop */}
        {user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="tc-nav-desktop">
            {user.user_metadata?.avatar_url && (
              <img src={user.user_metadata.avatar_url} alt="avatar" onClick={() => navigate('/profile')}
                style={{ width: 28, height: 28, borderRadius: '50%', border: `1.5px solid ${c.border}`, cursor: 'pointer' }} />
            )}
            {tierLabel && (
              <span style={{ fontSize: 11, background: c.accentBg, color: c.accent, border: `1px solid ${c.accentBorder}`, borderRadius: 99, padding: '2px 8px', fontWeight: 600 }}>{tierLabel}</span>
            )}
            {tier === 'admin' && (
              <span onClick={() => navigate('/admin')} style={{ fontSize: 12, color: c.secondary, cursor: 'pointer', padding: '4px 8px', borderRadius: 6, border: `1px solid ${c.border}` }}>Admin</span>
            )}
            <button onClick={handleLogout}
              style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: "'Inter', sans-serif" }}>
              {isDisconnecting ? '⏳' : 'Logout'}
            </button>
          </div>
        )}

        {/* Hamburger — mobile */}
        <button onClick={() => setMenuOpen(o => !o)} className="tc-nav-mobile"
          style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.text, width: 36, height: 36, borderRadius: 8, cursor: 'pointer', fontSize: 16, display: 'none', alignItems: 'center', justifyContent: 'center' }}>
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ position: 'absolute', top: 58, left: 0, right: 0, background: c.navBg, borderBottom: `1px solid ${c.border}`, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4, zIndex: 99 }}>
          {[...NAV_LINKS, ...MORE_LINKS].map(({ label, path }) => (
            <span key={path} onClick={() => { navigate(path); setMenuOpen(false); }}
              style={{ fontSize: 14, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', color: isActive(path) ? c.accent : c.text, background: isActive(path) ? c.accentBg : 'transparent', fontWeight: isActive(path) ? 600 : 400 }}>
              {label}
            </span>
          ))}
          {user && (
            <>
              <div style={{ height: 1, background: c.border, margin: '8px 0' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 12px' }}>
                {user.user_metadata?.avatar_url && <img src={user.user_metadata.avatar_url} alt="avatar" style={{ width: 28, height: 28, borderRadius: '50%' }} />}
                <span style={{ fontSize: 13, color: c.secondary, flex: 1 }}>{user.user_metadata?.full_name}</span>
                <button onClick={handleLogout} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Logout</button>
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .tc-nav-desktop { display: none !important; }
          .tc-nav-mobile  { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}